import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter } from "@angular/router";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { firstValueFrom } from "rxjs";

import { routes } from "./app.routes";
import { environment } from "../environments/environment";
import { APP_ROLES, AppRole } from "./core/roles/app-role.constants";

import { CoreUIFlashToastModule, CoreUIToastsV2Module } from "lib-klab-core-ui";

import {
  AUTH_UI_CONFIG,
  AuthUiConfig,
  LibKlabAuthUiModule,
} from "lib-klab-auth-ui";

import {
  ENV,
  EXECUTABLE_VERSION_PORT,
  ExecutableVersionPortAdapter,
  KLAB_IAM_ENV,
  MESSAGING_ENV,
  MESSAGING_PORT,
  MessagingPortAdapter,
  ORG_ENV,
  COMPANIES_PORT,
  CompaniesPortAdapter,
  LOCATIONS_PORT,
  SESSIONS_PORT,
  DEPARTMENTS_PORT,
  WORK_SCHEDULES_PORT,
  HOLIDAYS_PORT,
  REPORT_ENV,
  APP_REPORTS_PORT,
  WEB_REPORTS_PORT,
  CONNECTIVITY_REPORTS_PORT,
  ConnectivityReportsPortAdapter,
  DOCUMENT_ENV,
  NOTIFICATIONS_ENV,
  ADMIN_REPORTS_PORT,
  ADMIN_DASHBOARD_REPORTS_PORT,
  DEVICE_FLEET_PORT,
  AppActivityReportsPortAdapter,
  SessionsPortAdapter,
  LocationsPortAdapter,
  WorkSchedulesPortAdapter,
  HolidaysPortAdapter,
  DepartmentsPortAdapter,
  WebActivityReportsPortAdapter,
  AdminReportsPortAdapter,
  DeviceFleetPortAdapter,
  AdminDashboardReportsPortAdapter,
  TIMESHEET_PORT,
  KEYSTROKE_REPORTS_PORT,
  KeystrokeReportsPortAdapter,
  PASSWORD_RECOVERY_PORT,
  PasswordRecoveryPortAdapter,
  ROLES_PORT,
  RolesPortAdapter,
  PEOPLE_ENV,
  CHAT_DIRECTORY_PORT,
  ChatDirectoryPortAdapter,
  EMPLOYEES_PORT,
  EmployeesPortAdapter,
  EMPLOYEE_HIERARCHIES_PORT,
  EmployeeHierarchiesPortAdapter,
  PRODUCTIVITY_RULES_PORT,
  ProductivityRulesPortAdapter,
  REQUESTS_PORT,
  RequestsPortAdapter,
  authBearerInterceptor,
  sessionRevokedInterceptor,
  employeeHierarchyMutationApiKeyInterceptor,
  SessionRefreshOnFocusEffect,
  MASTER_DATA_ENV,
  MASTER_DATA_PORT,
  MasterDataPortAdapter,
  EMPLOYEE_ENV,
  HR_ENV,
  AGREEMENT_ENV,
  PAYROLL_ENV,
  STAFF_EMPLOYEES_PORT,
  StaffEmployeesPortAdapter,
  EMPLOYMENT_CONTRACTS_PORT,
  EmploymentContractsPortAdapter,
  EMPLOYMENT_AGREEMENTS_PORT,
  EmploymentAgreementsPortAdapter,
  STAFF_VACATIONS_PORT,
  StaffVacationsPortAdapter,
  STAFF_BENEFITS_PORT,
  StaffBenefitsPortAdapter,
  STAFF_BENEFIT_REQUESTS_PORT,
  StaffBenefitRequestsPortAdapter,
  STAFF_PAYROLL_PORT,
  StaffPayrollPortAdapter,
  ORGANIZATION_REFERENCE_PORT,
  OrganizationReferencePortAdapter,
  CONTENT_POPUPS_PORT,
  LocalContentPopupsPortAdapter,
  LEGAL_REPRESENTATIVES_PORT,
  LegalRepresentativesPortAdapter,
  PAYROLL_RESPONSIBLES_PORT,
  PayrollResponsiblesPortAdapter,
  COMPANY_PARAMETERS_PORT,
  CompanyParametersPortAdapter,
  COMPANY_LAW_MODIFICATIONS_PORT,
  CompanyLawModificationsPortAdapter,
  COMPANY_DIGITAL_PAYSLIPS_PORT,
  CompanyDigitalPayslipsPortAdapter,
  BANK_ACCOUNTS_PORT,
  BankAccountsPortAdapter,
  TREASURY_BANK_ACCOUNTS_PORT,
  TreasuryBankAccountsPortAdapter,
  TREASURY_PAYMENT_BATCHES_PORT,
  TreasuryPaymentBatchesPortAdapter,
  SURPLUS_REFUNDS_PORT,
  SurplusRefundsPortAdapter,
  EXPENSE_REPORTS_PORT,
  ExpenseReportsPortAdapter,
  EXPENSE_CATEGORIES_PORT,
  ExpenseCategoriesPortAdapter,
  PURCHASE_ORDERS_PORT,
  PurchaseOrdersPortAdapter,
  REQUIREMENTS_PORT,
  RequirementsPortAdapter,
  PRIORITY_LEVELS_PORT,
  PriorityLevelsPortAdapter,
  REQUIREMENT_WORKFLOW_PORT,
  RequirementWorkflowPortAdapter,
  REQUIREMENT_ATTACHMENTS_PORT,
  RequirementAttachmentsPortAdapter,
  REQUIREMENT_PARAMETERS_PORT,
  RequirementParametersPortAdapter,
  POSITION_CATALOG_PORT,
  PositionCatalogPortAdapter,
  ACCOUNTING_ENV,
  TREASURY_ENV,
  INTEGRATION_ENV,
  AUDIT_ENV,
} from "lib-klab-core";

import {
  IAM_BASE_PATH,
  ORG_BASE_PATH,
  PEOPLE_BASE_PATH,
  MASTER_DATA_BASE_PATH,
  STAFF_EMPLOYEE_BASE_PATH,
  STAFF_ORGANIZATION_BASE_PATH,
  STAFF_HR_BASE_PATH,
  STAFF_AGREEMENT_BASE_PATH,
  STAFF_PAYROLL_BASE_PATH,
  STAFF_TREASURY_BASE_PATH,
  STAFF_PROCUREMENT_BASE_PATH,
  STAFF_REQUIREMENT_BASE_PATH,
  STAFF_ACCOUNTING_BASE_PATH,
  REPORT_BASE_PATH,
} from "lib-klab-sdks";
import { TimeSheetReportsPortAdapter } from "lib-klab-core";
import { staffMockFallbackInterceptor } from "./core/http/staff-mock-fallback.interceptor";

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        authBearerInterceptor,
        sessionRevokedInterceptor,
        employeeHierarchyMutationApiKeyInterceptor,
        staffMockFallbackInterceptor,
      ]),
    ),
    provideAnimations(),

    importProvidersFrom(
      CoreUIToastsV2Module,
      CoreUIFlashToastModule,
      LibKlabAuthUiModule,
    ),

    { provide: ENV, useValue: environment },
    { provide: KLAB_IAM_ENV, useValue: environment.micros.iam },
    { provide: ORG_ENV, useValue: environment.micros.organization },
    { provide: REPORT_ENV, useValue: environment.micros.report },
    { provide: PEOPLE_ENV, useValue: environment.micros.people },
    { provide: MESSAGING_ENV, useValue: environment.micros.messaging },
    { provide: DOCUMENT_ENV, useValue: environment.micros.document },
    { provide: NOTIFICATIONS_ENV, useValue: environment.micros.notifications },
    { provide: MASTER_DATA_ENV, useValue: environment.micros.masterData! },
    { provide: EMPLOYEE_ENV, useValue: environment.micros.employee! },
    { provide: HR_ENV, useValue: environment.micros.hr! },
    { provide: AGREEMENT_ENV, useValue: environment.micros.agreement! },
    { provide: PAYROLL_ENV, useValue: environment.micros.payroll! },
    { provide: ACCOUNTING_ENV, useValue: environment.micros.accounting! },
    { provide: TREASURY_ENV, useValue: environment.micros.treasury! },
    { provide: INTEGRATION_ENV, useValue: environment.micros.integration! },
    { provide: AUDIT_ENV, useValue: environment.micros.audit! },

    {
      provide: IAM_BASE_PATH,
      useValue: `${environment.micros.iam.host}${environment.micros.iam.apiBasePath}`,
    },
    {
      provide: ORG_BASE_PATH,
      useValue: `${environment.micros.organization.host}${environment.micros.organization.apiBasePath}`,
    },
    {
      provide: REPORT_BASE_PATH,
      useValue: `${environment.micros.report.host}${environment.micros.report.apiBasePath}`,
    },
    {
      provide: PEOPLE_BASE_PATH,
      useValue: `${environment.micros.people.host}${environment.micros.people.apiBasePath}`,
    },
    {
      provide: MASTER_DATA_BASE_PATH,
      useValue: `${environment.micros.masterData!.host}${environment.micros.masterData!.apiBasePath}`,
    },
    {
      provide: STAFF_EMPLOYEE_BASE_PATH,
      useValue: `${environment.micros.employee!.host}${environment.micros.employee!.apiBasePath}`,
    },
    {
      provide: STAFF_ORGANIZATION_BASE_PATH,
      useValue: `${environment.micros.staffOrganization.host}${environment.micros.staffOrganization.apiBasePath}`,
    },
    {
      provide: STAFF_HR_BASE_PATH,
      useValue: `${environment.micros.hr!.host}${environment.micros.hr!.apiBasePath}`,
    },
    {
      provide: STAFF_AGREEMENT_BASE_PATH,
      useValue: `${environment.micros.agreement!.host}${environment.micros.agreement!.apiBasePath}`,
    },
    {
      provide: STAFF_PAYROLL_BASE_PATH,
      useValue: `${environment.micros.payroll!.host}${environment.micros.payroll!.apiBasePath}`,
    },
    {
      provide: STAFF_TREASURY_BASE_PATH,
      useValue: `${environment.micros.treasury!.host}${environment.micros.treasury!.apiBasePath}`,
    },
    {
      provide: STAFF_PROCUREMENT_BASE_PATH,
      useValue: `${environment.micros.procurement!.host}${environment.micros.procurement!.apiBasePath}`,
    },
    {
      provide: STAFF_REQUIREMENT_BASE_PATH,
      useValue: `${environment.micros.requirement!.host}${environment.micros.requirement!.apiBasePath}`,
    },
    {
      provide: STAFF_ACCOUNTING_BASE_PATH,
      useValue: `${environment.micros.accounting!.host}${environment.micros.accounting!.apiBasePath}`,
    },

    { provide: SESSIONS_PORT, useExisting: SessionsPortAdapter },
    { provide: COMPANIES_PORT, useExisting: CompaniesPortAdapter },
    { provide: MASTER_DATA_PORT, useExisting: MasterDataPortAdapter },
    { provide: STAFF_EMPLOYEES_PORT, useExisting: StaffEmployeesPortAdapter },
    {
      provide: EMPLOYMENT_CONTRACTS_PORT,
      useExisting: EmploymentContractsPortAdapter,
    },
    {
      provide: EMPLOYMENT_AGREEMENTS_PORT,
      useExisting: EmploymentAgreementsPortAdapter,
    },
    {
      provide: STAFF_VACATIONS_PORT,
      useExisting: StaffVacationsPortAdapter,
    },
    {
      provide: STAFF_BENEFITS_PORT,
      useExisting: StaffBenefitsPortAdapter,
    },
    {
      provide: STAFF_BENEFIT_REQUESTS_PORT,
      useExisting: StaffBenefitRequestsPortAdapter,
    },
    {
      provide: STAFF_PAYROLL_PORT,
      useExisting: StaffPayrollPortAdapter,
    },
    {
      provide: ORGANIZATION_REFERENCE_PORT,
      useExisting: OrganizationReferencePortAdapter,
    },
    {
      provide: CONTENT_POPUPS_PORT,
      useExisting: LocalContentPopupsPortAdapter,
    },
    {
      provide: LEGAL_REPRESENTATIVES_PORT,
      useExisting: LegalRepresentativesPortAdapter,
    },
    {
      provide: PAYROLL_RESPONSIBLES_PORT,
      useExisting: PayrollResponsiblesPortAdapter,
    },
    {
      provide: COMPANY_PARAMETERS_PORT,
      useExisting: CompanyParametersPortAdapter,
    },
    {
      provide: COMPANY_LAW_MODIFICATIONS_PORT,
      useExisting: CompanyLawModificationsPortAdapter,
    },
    {
      provide: COMPANY_DIGITAL_PAYSLIPS_PORT,
      useExisting: CompanyDigitalPayslipsPortAdapter,
    },
    {
      provide: BANK_ACCOUNTS_PORT,
      useExisting: BankAccountsPortAdapter,
    },
    {
      provide: TREASURY_BANK_ACCOUNTS_PORT,
      useExisting: TreasuryBankAccountsPortAdapter,
    },
    {

      provide: TREASURY_PAYMENT_BATCHES_PORT,
      useExisting: TreasuryPaymentBatchesPortAdapter,
    },
    {
      provide: SURPLUS_REFUNDS_PORT,
      useExisting: SurplusRefundsPortAdapter,

    },
    {
      provide: EXPENSE_REPORTS_PORT,
      useExisting: ExpenseReportsPortAdapter,
    },
    {
      provide: EXPENSE_CATEGORIES_PORT,
      useExisting: ExpenseCategoriesPortAdapter,
    },
    {
      provide: PURCHASE_ORDERS_PORT,
      useExisting: PurchaseOrdersPortAdapter,
    },
    {
      provide: REQUIREMENTS_PORT,
      useExisting: RequirementsPortAdapter,
    },
    {
      provide: PRIORITY_LEVELS_PORT,
      useExisting: PriorityLevelsPortAdapter,
    },
    {
      provide: REQUIREMENT_WORKFLOW_PORT,
      useExisting: RequirementWorkflowPortAdapter,
    },
    {
      provide: REQUIREMENT_ATTACHMENTS_PORT,
      useExisting: RequirementAttachmentsPortAdapter,
    },
    {
      provide: REQUIREMENT_PARAMETERS_PORT,
      useExisting: RequirementParametersPortAdapter,
    },
    {
      provide: POSITION_CATALOG_PORT,
      useExisting: PositionCatalogPortAdapter,
    },
    { provide: LOCATIONS_PORT, useExisting: LocationsPortAdapter },
    { provide: WORK_SCHEDULES_PORT, useExisting: WorkSchedulesPortAdapter },
    { provide: HOLIDAYS_PORT, useExisting: HolidaysPortAdapter },
    { provide: DEPARTMENTS_PORT, useExisting: DepartmentsPortAdapter },
    {
      provide: EXECUTABLE_VERSION_PORT,
      useExisting: ExecutableVersionPortAdapter,
    },
    { provide: APP_REPORTS_PORT, useExisting: AppActivityReportsPortAdapter },
    { provide: WEB_REPORTS_PORT, useExisting: WebActivityReportsPortAdapter },
    {
      provide: CONNECTIVITY_REPORTS_PORT,
      useExisting: ConnectivityReportsPortAdapter,
    },
    { provide: ADMIN_REPORTS_PORT, useExisting: AdminReportsPortAdapter },
    { provide: DEVICE_FLEET_PORT, useExisting: DeviceFleetPortAdapter },
    {
      provide: ADMIN_DASHBOARD_REPORTS_PORT,
      useExisting: AdminDashboardReportsPortAdapter,
    },
    { provide: TIMESHEET_PORT, useExisting: TimeSheetReportsPortAdapter },
    {
      provide: KEYSTROKE_REPORTS_PORT,
      useExisting: KeystrokeReportsPortAdapter,
    },
    {
      provide: PASSWORD_RECOVERY_PORT,
      useExisting: PasswordRecoveryPortAdapter,
    },
    { provide: ROLES_PORT, useExisting: RolesPortAdapter },
    { provide: CHAT_DIRECTORY_PORT, useExisting: ChatDirectoryPortAdapter },
    { provide: EMPLOYEES_PORT, useExisting: EmployeesPortAdapter },
    {
      provide: EMPLOYEE_HIERARCHIES_PORT,
      useExisting: EmployeeHierarchiesPortAdapter,
    },
    {
      provide: PRODUCTIVITY_RULES_PORT,
      useExisting: ProductivityRulesPortAdapter,
    },
    { provide: REQUESTS_PORT, useExisting: RequestsPortAdapter },
    { provide: MESSAGING_PORT, useExisting: MessagingPortAdapter },

    {
      provide: AUTH_UI_CONFIG,
      useValue: {
        appKind: "admin",

        storageTokenKey: "c2Vzc2lvBg",

        postLoginRedirectUrl: "/admin",
        redirectByRoleId: {
          [APP_ROLES[AppRole.Accountant].id]: "/admin/accounting",
        },
        loginUrl: "/auth/login",
        forbiddenRedirectUrl: "/auth/login",
      } satisfies AuthUiConfig,
    },

    provideAppInitializer(() => {
      const effect = inject(SessionRefreshOnFocusEffect);
      effect.init();
      return firstValueFrom(effect.refreshIfLoggedIn(), {
        defaultValue: undefined,
      }).catch(() => void 0);
    }),
  ],
};
